import { useEffect, useState } from 'react';
import { Typography } from '../../components/typography/typography';
import './accounting.scss';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
  ItemGeneralLedgerTransactionType,
  ItemType,
} from '../../clients/quipli-service/accounting/accounting';
import { ItemGeneralLedgerAccountInput } from './item-general-ledger-account-input';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';

export const AccountingSettings = () => {
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>();
  const [customProductEnabled, setCustomProductEnabled] = useState<Configuration>();
  const { listAccounts } = useAccountingApiV1();
  const { getConfiguration } = useAPI();

  useEffect(() => {
    listAccounts().then(setAccounts);
    getConfiguration('CONF_ENABLE_CUSTOM_PRODUCTS').then(setCustomProductEnabled);
  }, []);

  return (
    <div className="accounting container-v2">
      <div className="accounting-header">
        <div className="flex justify-between items-center">
          <Typography type="display-sm" weight="medium">
            Accounting Settings
          </Typography>
        </div>
      </div>

      <div className="bg-white rounded-[9px] p-[16px] mt-[24px] accounting-body">
        <div>
          <ItemGeneralLedgerAccountInput label="Default Income Account" accounts={accounts} />
          <Typography type="text-xs" className="input-description">
            The default income account that will be used for all Quipli products and services unless a more specific
            configuration exists.
          </Typography>
        </div>
        <div>
          <ItemGeneralLedgerAccountInput
            transactionType={ItemGeneralLedgerTransactionType.Rental}
            label="Default Rental Income Account"
            accounts={accounts}
          />
          <Typography type="text-xs" className="input-description">
            The income account that will be used for all rental line items. If blank default income account will be
            used.
          </Typography>
        </div>
        <div>
          <ItemGeneralLedgerAccountInput
            transactionType={ItemGeneralLedgerTransactionType.Sale}
            label="Default Sale Income Account"
            accounts={accounts}
          />
          <Typography type="text-xs" className="input-description">
            The income account that will be used for all sale line items. If blank default income account will be used.
          </Typography>
        </div>
        <div>
          <ItemGeneralLedgerAccountInput
            transactionType={ItemGeneralLedgerTransactionType.Expense}
            label="Default Expense Account"
            accounts={accounts?.filter((acc) => acc.accountType === GeneralLedgerAccountType.Expense)}
          />
          <Typography type="text-xs" className="input-description">
            The account that will be used for all expense line items. If blank default income account will be used.
          </Typography>
        </div>
        {customProductEnabled?.value === '1' && (
          <div>
            <ItemGeneralLedgerAccountInput
              itemType={ItemType.CustomProduct}
              label="Default Custom Product Income Account"
              accounts={accounts}
            />
            <Typography type="text-xs" className="input-description">
              The income account that will be used for all custom product line items. If blank default income account
              will be used.
            </Typography>
          </div>
        )}
        <div>
          <ItemGeneralLedgerAccountInput
            itemType={ItemType.Freight}
            label="Delivery Income Account"
            accounts={accounts}
          />
          <Typography type="text-xs" className="input-description">
            The income account that will be used for all delivery line items. If blank default income account will be
            used.
          </Typography>
        </div>
        <div>
          <ItemGeneralLedgerAccountInput
            itemType={ItemType.Upcharge}
            label="Up-Charge Income Account"
            accounts={accounts}
          />
          <Typography type="text-xs" className="input-description">
            The income account that will be used for all up-charge line items. If blank default income account will be
            used.
          </Typography>
        </div>
        <div>
          <ItemGeneralLedgerAccountInput
            itemType={ItemType.DamageWaiver}
            label="Damage Waiver Income Account"
            accounts={accounts}
          />
          <Typography type="text-xs" className="input-description">
            The income account that will be used for all damage waiver line items. If blank default income account will
            be used.
          </Typography>
        </div>
        <div>
          <ItemGeneralLedgerAccountInput
            itemType={ItemType.Discount}
            label="Discount Income Account"
            accounts={accounts}
          />
          <Typography type="text-xs" className="input-description">
            The income account that will be used for additional discounts and default for coupons unless overridden. If
            blank default income account will be used.
          </Typography>
        </div>
        <div>
          <ItemGeneralLedgerAccountInput itemType={ItemType.Refund} label="Refund From Account" accounts={accounts} />
          <Typography type="text-xs" className="input-description">
            The income account that will be used for all refund line items. If blank default income account will be
            used.
          </Typography>
        </div>
        <div>
          <ItemGeneralLedgerAccountInput
            itemType={ItemType.CreditCardFee}
            label="Processing Fee Income Account"
            accounts={accounts}
          />
          <Typography type="text-xs" className="input-description">
            The income account that will be used for credit card and bank processing fees unless overridden. If blank
            default income account will be used.
          </Typography>
        </div>
      </div>
    </div>
  );
};
